import { put, all, call, takeLatest, select } from "redux-saga/effects";
import {
  REQNotificationList,
  RESNotificationList,
  FailNotificationList,
  SENDxNotification,
  DELxNotification,
  SETxNotificationStatus,
  REQEventCounts,
  RESEventCounts,
  REQCsatSetting,
  REQSaveCsatSetting,
  RESCsatSetting,
  FailCsatSetting
} from "./actions";
import * as actions from "../../Authentication/redux/actions";
import { authHeaders, notificationInit, notificationRequest, readError } from "../api";

function* adminSession() {
  const accessToken = yield select(state => state.auth.accessToken);
  const tGUID = yield select(state => state.auth.GUID);
  return authHeaders(accessToken, tGUID);
}

/*
 * GET /api/v1/admin/notifications?search=&status=&sendStatus=&origin=&eventType=&page=&limit=
 *
 * `origin` is the list's tab (ADMIN = Manual, AUTOMATED = Automatic); `eventType` the Automatic
 * tab's event filter. Both are applied by the backend so each tab pages and counts on its own.
 *
 * `silent` marks the screen's background refresh: if it fails, the rows already shown stay and
 * the next refresh tries again, rather than an error banner appearing out of nowhere.
 */
function* fetchNotificationListWorker({ payload }) {
  const filters = payload || {};
  try {
    const headers = yield adminSession();
    const query = {
      ...(filters.search ? { search: filters.search } : {}),
      ...(filters.status && filters.status !== "ALL" ? { status: filters.status } : {}),
      ...(filters.sendStatus && filters.sendStatus !== "ALL" ? { sendStatus: filters.sendStatus } : {}),
      ...(filters.origin ? { origin: filters.origin } : {}),
      ...(filters.eventType ? { eventType: filters.eventType } : {}),
      page: String(filters.page || 1),
      limit: String(filters.limit || 20)
    };

    const call = notificationRequest("list", { query });
    const request = yield fetch(call.url, notificationInit(call, headers));

    if (request.status === 401) {
      yield put(actions.autoSignOut());
      return;
    }

    if (!request.ok) {
      const message = yield readError(request);
      if (!filters.silent) {
        yield put(FailNotificationList({ message }));
      }
      return;
    }

    const response = yield request.json();
    yield put(RESNotificationList(response));
  } catch (error) {
    if (!filters.silent) {
      yield put(FailNotificationList({ message: error.message }));
    }
  }
}

/* POST /api/v1/admin/notifications/:id/send */
function* sendNotificationWorker({ payload }) {
  try {
    const headers = yield adminSession();
    const call = notificationRequest("send", { id: payload.id });
    const request = yield fetch(call.url, notificationInit(call, headers));

    if (request.status === 401) {
      yield put(actions.autoSignOut());
      return;
    }

    if (!request.ok) {
      const message = yield readError(request);
      yield put(FailNotificationList({ message }));
      return;
    }

    yield fetchNotificationListWorker({ payload: payload.filters });
  } catch (error) {
    yield put(FailNotificationList({ message: error.message }));
  }
}

/* DELETE /api/v1/admin/notifications/:id */
function* deleteNotificationWorker({ payload }) {
  try {
    const headers = yield adminSession();
    const call = notificationRequest("remove", { id: payload.id });
    const request = yield fetch(call.url, notificationInit(call, headers));

    if (request.status === 401) {
      yield put(actions.autoSignOut());
      return;
    }

    if (!request.ok) {
      const message = yield readError(request);
      yield put(FailNotificationList({ message }));
      return;
    }

    yield fetchNotificationListWorker({ payload: payload.filters });
  } catch (error) {
    yield put(FailNotificationList({ message: error.message }));
  }
}

/*
 * PUT /api/v1/admin/notifications/:id/status
 *
 * The only change a delivered campaign accepts: deactivating it hides it from every
 * customer's inbox while its Reach / View history stays.
 */
function* setNotificationStatusWorker({ payload }) {
  try {
    const headers = yield adminSession();
    const call = notificationRequest("setStatus", {
      id: payload.id,
      payload: { status: payload.status }
    });
    const request = yield fetch(call.url, notificationInit(call, headers));

    if (request.status === 401) {
      yield put(actions.autoSignOut());
      return;
    }

    if (!request.ok) {
      const message = yield readError(request);
      yield put(FailNotificationList({ message }));
      return;
    }

    yield fetchNotificationListWorker({ payload: payload.filters });
  } catch (error) {
    yield put(FailNotificationList({ message: error.message }));
  }
}

// The total of one event's automatic notifications: a one-row page, read for its pagination total.
// A failed request gives null, which the page shows as a dash.
async function fetchEventCount(code, headers) {
  const spec = notificationRequest("list", { query: { origin: "AUTOMATED", eventType: code, page: "1", limit: "1" } });
  const request = await fetch(spec.url, notificationInit(spec, headers));
  if (!request.ok) {
    return null;
  }
  const response = await request.json();
  return response.pagination ? response.pagination.total : null;
}

function* fetchEventCountsWorker({ payload }) {
  const codes = payload || [];
  try {
    const headers = yield adminSession();
    const totals = yield all(codes.map(code => call(fetchEventCount, code, headers)));
    const counts = {};
    codes.forEach((code, index) => {
      counts[code] = totals[index];
    });
    yield put(RESEventCounts(counts));
  } catch (error) {
    yield put(RESEventCounts({}));
  }
}

/*
 * GET the automatic CSAT settings, or PUT a change to them. `payload` is the body of the PUT, a
 * partial { events, viewPortfolioDays }. The answer is the whole setting either way.
 */
function* csatSettingWorker(action, payload) {
  try {
    const headers = yield adminSession();
    const call = notificationRequest(action, { payload });
    const request = yield fetch(call.url, notificationInit(call, headers));

    if (request.status === 401) {
      yield put(actions.autoSignOut());
      return;
    }

    if (!request.ok) {
      const message = yield readError(request);
      yield put(FailCsatSetting({ message }));
      return;
    }

    const response = yield request.json();
    yield put(RESCsatSetting(response));
  } catch (error) {
    yield put(FailCsatSetting({ message: error.message }));
  }
}

function* fetchCsatSettingWorker() {
  yield csatSettingWorker("csatSetting", undefined);
}

function* saveCsatSettingWorker({ payload }) {
  yield csatSettingWorker("updateCsatSetting", payload);
}

export function* notificationListSaga() {
  yield all([
    takeLatest(REQEventCounts, fetchEventCountsWorker),
    takeLatest(REQCsatSetting, fetchCsatSettingWorker),
    takeLatest(REQSaveCsatSetting, saveCsatSettingWorker),
    takeLatest(REQNotificationList, fetchNotificationListWorker),
    takeLatest(SENDxNotification, sendNotificationWorker),
    takeLatest(DELxNotification, deleteNotificationWorker),
    takeLatest(SETxNotificationStatus, setNotificationStatusWorker)
  ]);
}
